import { Camera, Hammer } from "lucide-react";
import { useT } from "@/context/ContentContext";

const POINTS = [
    { t: "Proposta e documentação.", d: "A proposta, as condições e as garantias, sempre à mão. É também aí que adjudica." },
    { t: "Atualizações com fotografias.", d: "Durante a execução, vê a evolução da obra em fotografias." },
    { t: "O que fizemos. O que acontece a seguir.", d: "Cada atualização explica claramente o que foi feito e qual é o próximo passo." },
    { t: "Resumo semanal e histórico.", d: "No fim de cada semana de obra em que existam atualizações, recebe um resumo por email. Depois da entrega, o histórico continua disponível." },
];

// Ilustração da Área do Cliente (mesmos rótulos da página real; sem dados de clientes).
const MOCK_PHOTOS = [
    "https://images.unsplash.com/photo-1714860534425-7ce04e013dec?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
    "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
    "https://images.unsplash.com/photo-1763485955998-a9284d042d50?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
];

export default function HomeAcompanhamento() {
    const t = useT();
    return (
        <section className="py-16 sm:py-24" data-testid="home-acompanhamento">
            <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                <div className="bg-[hsl(var(--forest))] rounded-[2rem] px-6 py-12 sm:p-16 text-white overflow-hidden">
                    <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
                        <div>
                            <div className="eyebrow-light mb-6"><Camera className="w-3.5 h-3.5" /> {t("home.acomp.eyebrow", "Acompanhamento")}</div>
                            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.02]" data-testid="acomp-title">
                                {t("home.acomp.title", "A sua obra,")}<br />
                                <span className="font-serif-italic text-[hsl(var(--copper))]">{t("home.acomp.title_accent", "sempre consigo")}</span>
                                <span className="text-[hsl(var(--copper))]">.</span>
                            </h2>
                            <p className="text-white/80 text-base sm:text-lg mt-6 leading-relaxed max-w-2xl" data-testid="acomp-intro">
                                {t("home.acomp.intro", "Não deve ter de nos ligar para saber como está a sua obra. Por isso, durante todo o processo mantemo-lo informado e damos-lhe acesso a uma Área do Cliente privada, através de um link pessoal — sem passwords nem aplicações.")}
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3 mt-10">
                                {POINTS.map((p, i) => (
                                    <div key={p.t} className="bg-white/5 border border-white/15 rounded-2xl p-5 sm:p-6 hover:bg-white/10 transition-colors" data-testid={`acomp-point-${i + 1}`}>
                                        <div className="font-mono text-xs text-[hsl(var(--copper))] mb-2">{`0${i + 1}`}</div>
                                        <h3 className="font-display text-lg font-bold leading-snug mb-2">{t(`home.acomp.p${i + 1}.title`, p.t)}</h3>
                                        <p className="text-white/70 text-sm leading-relaxed">{t(`home.acomp.p${i + 1}.desc`, p.d)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <PhoneMockup />
                    </div>
                    <div className="mt-12 pt-10 border-t border-white/15 grid lg:grid-cols-[1.15fr_0.85fr] gap-6 lg:gap-16 items-end">
                        <p className="font-display text-2xl sm:text-3xl font-bold leading-tight tracking-tight" data-testid="acomp-highlight">
                            <span className="text-white/60">{t("home.acomp.highlight_1", "Acompanhar não é responder quando nos pergunta.")}</span>{" "}
                            {t("home.acomp.highlight_2", "É mantê-lo informado antes de precisar de perguntar.")}
                        </p>
                        <p className="text-white/80 text-base sm:text-lg leading-relaxed" data-testid="acomp-closing">
                            {t("home.acomp.closing", "Pode ligar-nos sempre que quiser. A ideia é não precisar.")}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function PhoneMockup() {
    const label = "text-[8px] uppercase tracking-[0.2em] font-mono";
    return (
        <div className="mx-auto w-full max-w-[290px] lg:max-w-[310px]" aria-hidden="true" data-testid="acomp-phone-mockup">
            <div className="rounded-[2.75rem] bg-[hsl(var(--forest-deep))] p-2.5 ring-1 ring-white/15 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]">
                <div className="rounded-[2.25rem] bg-[hsl(var(--cream))] overflow-hidden">
                    <div className="flex justify-center pt-2.5 pb-1">
                        <span className="w-20 h-1.5 rounded-full bg-[hsl(var(--forest-deep))]/85" />
                    </div>
                    <div className="px-3.5 pb-5 space-y-2.5">
                        <div className="flex items-center justify-between px-1 pt-1">
                            <span className="logo-mark text-lg text-[hsl(var(--forest))]">H<span className="dot">.</span></span>
                            <span className={`${label} text-stone-500`}>Área do Cliente</span>
                        </div>
                        <div className="bg-white rounded-2xl border border-stone-200 p-3.5">
                            <div className={`flex items-center gap-1.5 ${label} text-amber-700`}>
                                <Hammer className="w-2.5 h-2.5" /> Execução em curso
                            </div>
                            <div className="font-display text-base font-bold text-[hsl(var(--forest))] leading-tight mt-1">A sua obra está a avançar.</div>
                        </div>
                        <div className="bg-white rounded-2xl border border-stone-200 p-3.5">
                            <div className={`${label} text-[hsl(var(--copper))] mb-2`}>Acompanhamento da obra</div>
                            <div className={`${label} text-stone-500`}>Última atualização</div>
                            <div className="font-display text-sm font-bold text-[hsl(var(--forest))]">Hoje, 16:40</div>
                            <div className="mt-3 pl-2.5 border-l-2 border-[hsl(var(--copper))]/30">
                                <div className="grid grid-cols-3 gap-1">
                                    {MOCK_PHOTOS.map((src) => (
                                        <img key={src} src={src} alt="" loading="lazy" className="w-full aspect-square object-cover rounded-md" />
                                    ))}
                                </div>
                                <div className={`mt-2.5 ${label} text-stone-500`}>O que fizemos</div>
                                <p className="text-[10px] text-stone-800 leading-snug">Concluímos as alterações de canalização e iniciámos a preparação das paredes.</p>
                                <div className={`mt-2 ${label} text-stone-500`}>O que acontece a seguir</div>
                                <p className="text-[10px] text-stone-800 leading-snug">Aplicação dos revestimentos.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
